// ============================================================================
// CENTRALIZED QUERY KEYS
// Single source of truth for all React Query cache keys
// ============================================================================

/**
 * Query key factory for consistent cache invalidation across the app.
 * Use these helpers instead of inline arrays to ensure cache consistency.
 */
export const queryKeys = {
  // ============== Properties ==============
  properties: {
    all: ['properties'] as const,
    archived: ['archived-properties'] as const,
    detail: (propertyId: string) => ['property', propertyId] as const,
  },

  // ============== Cleaning Tasks ==============
  cleaningTasks: {
    all: ['cleaning-tasks'] as const,
    byProperty: (propertyId: string) => ['cleaning-tasks', propertyId] as const,
    byCompany: (companyId: string) => ['company-cleaning-tasks', companyId] as const,
    /** Prefix for useInProgressCleaningTasks / useActiveCleaningTask (user id in full key). */
    inProgress: () => ['in-progress-cleaning-tasks'] as const,
    /** @deprecated Use inProgress() — invalidates all in-progress rows for the session user. */
    active: (_cleanerId: string) => ['in-progress-cleaning-tasks'] as const,
    current: (propertyId: string, cleanerId?: string) => 
      ['current-cleaning-task', propertyId, cleanerId] as const,
    upcoming: (cleanerId: string, excludeTaskId?: string) => 
      ['upcoming-cleaning-tasks', cleanerId, excludeTaskId] as const,
  },

  // ============== Reservations ==============
  reservations: {
    all: ['reservations'] as const,
    byProperty: (propertyId: string) => ['reservations', propertyId] as const,
    past: (propertyId: string) => ['past-reservations', propertyId] as const,
  },

  // ============== Cleaners & Assignments ==============
  cleaners: {
    all: ['cleaners'] as const,
    detail: (cleanerId: string) => ['cleaner', cleanerId] as const,
    assignments: (propertyId: string) => ['cleaner-assignments', propertyId] as const,
    performance: (propertyId: string) => ['cleaner-performance', propertyId] as const,
    ratings: (cleanerId: string) => ['cleaner-ratings', cleanerId] as const,
    ratedTasks: (cleanerId: string) => ['cleaner-rated-tasks', cleanerId] as const,
  },

  // ============== Cleaning Companies ==============
  companies: {
    all: ['cleaning-companies'] as const,
    detail: (companyId: string) => ['company', companyId] as const,
    cleaners: (companyId: string) => ['company-cleaners', companyId] as const,
    propertyAssignment: (propertyId: string) => ['property-cleaning-assignment', propertyId] as const,
    properties: (companyId: string) => ['company-properties', companyId] as const,
  },

  // ============== Checklists ==============
  checklists: {
    all: ['checklists'] as const,
    byProperty: (propertyId: string) => ['checklists', propertyId] as const,
    rules: (propertyId: string) => ['checklist-rules', propertyId] as const,
  },

  // ============== Property Rooms (canonical per property) ==============
  propertyRooms: {
    byProperty: (propertyId: string) => ['property-rooms', propertyId] as const,
  },

  // ============== Inventory ==============
  inventory: {
    all: ['inventory'] as const,
    byProperty: (propertyId: string) => ['inventory', propertyId] as const,
    replenishmentSuggestions: (propertyId: string) => ['replenishment-suggestions', propertyId] as const,
  },

  // ============== Unit baseline (Slice 20) ==============
  unitItems: {
    byProperty: (propertyId: string) => ['property-unit-items', propertyId] as const,
  },
  unitCheckMarks: {
    byTask: (taskId: string) => ['unit-check-marks', taskId] as const,
  },
  // Slice 37 — Walk quick checks + live photo asks
  walkQuickChecks: {
    byProperty: (propertyId: string) => ['property-walk-quick-checks', propertyId] as const,
  },
  unitPhotoAsks: {
    byTask: (taskId: string) => ['unit-photo-asks', taskId] as const,
  },

  // ============== Cleaning Submissions ==============
  submissions: {
    all: ['cleaning-submissions'] as const,
    byProperty: (propertyId: string) => ['cleaning-submissions', propertyId] as const,
  },

  // ============== Maintenance ==============
  maintenance: {
    all: ['maintenance-issues'] as const,
    byProperty: (propertyId: string) => ['maintenance-issues', propertyId] as const,
    appliances: (propertyId: string) => ['appliances', propertyId] as const,
  },

  // ============== Property Vendors ==============
  vendors: {
    all: ['property-vendors'] as const,
    byProperty: (propertyId: string) => ['property-vendors', propertyId] as const,
  },

  // ============== Photos ==============
  photos: {
    reference: (propertyId: string, checklistId: string | null = null) =>
      ['reference-photos', propertyId, checklistId ?? 'legacy'] as const,
    taskReference: (propertyId: string, checklistId: string | null = null) =>
      ['task-reference-photos', propertyId, checklistId ?? 'legacy'] as const,
  },

  // ============== Property Guide ==============
  propertyGuide: {
    byProperty: (propertyId: string) => ['property-guide', propertyId] as const,
  },

  // ============== Preventative Maintenance ==============
  preventativeMaintenance: {
    templates: ['preventative-maintenance-templates'] as const,
    byProperty: (propertyId: string) => ['property-preventative-maintenance', propertyId] as const,
  },

  // ============== Messaging ==============
  messages: {
    inbox: (userId: string) => ['inbox', userId] as const,
    thread: (threadId: string) => ['messages', threadId] as const,
    unreadCounts: (userId: string) => ['unread-counts', userId] as const,
  },

  // ============== Notifications ==============
  notifications: {
    all: ['notifications'] as const,
    byOwner: (ownerId: string) => ['owner-notifications', ownerId] as const,
  },

  // ============== Billing & Purchases ==============
  billing: {
    profile: (ownerId: string) => ['billing-profile', ownerId] as const,
    purchases: {
      byCleaner: (propertyId: string) => ['cleaner-purchases', propertyId] as const,
      byOwner: (ownerId: string) => ['owner-purchases', ownerId] as const,
      all: ['all-cleaner-purchases'] as const,
    },
  },

  // ============== User & Profile ==============
  user: {
    profile: ['profile'] as const,
    dashboardStats: (ownerId: string) => ['dashboard-stats', ownerId] as const,
    payoutStatus: (userId: string) => ['payout-status', userId] as const,
  },

  // ============== Invoices (payee payments) ==============
  invoices: {
    byProperty: (propertyId: string) => ['invoices-by-property', propertyId] as const,
    detail: (invoiceId: string) => ['invoice', invoiceId] as const,
    myPayeeInvoices: (tab: 'pending' | 'paid') => ['my-payee-invoices', tab] as const,
  },
} as const;

// Type helper for query key inference
export type QueryKeys = typeof queryKeys;
